<template>
  <div class="post-body">
    <p class="post-lead">
      This is one a lot of operators have asked for. You can now offer multiple durations for the same
      event, with completely separate pricing for each length, and OTC handles the calendar, resources and
      availability for whichever length the customer picks.
    </p>

    <h2>What we've added</h2>
    <p>For example, a single event can now offer:</p>
    <ul>
      <li>60 minutes (as the default)</li>
      <li>90 minutes</li>
      <li>120 minutes</li>
    </ul>
    <p>
      Each duration has its own pricing categories, participant tiers, and prices. The 90 minute session is
      not the 60 minute price multiplied up. Adults can pay one rate for an hour and a different rate for
      ninety minutes, children can be free on the short session only, and a team rate can exist on the two
      hour session alone.
    </p>
    <p>
      When a customer books online, they first choose their time slot, then choose the duration they want.
      OTC automatically shows the pricing for that specific duration. Your team sees the same duration
      selector when making an internal booking, adding a booking to an order, or moving a booking to
      another slot.
    </p>

    <h2>Why this matters</h2>
    <p>
      Previously, a slot was whatever length your schedule was built on. If you wanted to sell a longer
      version of the same experience, you had to create a separate event just to support the different
      length or pricing. That split the calendar, split the reporting, and made the booking site harder to
      read.
    </p>
    <ul>
      <li>
        <strong>More revenue from the same slot.</strong> A longer session at a higher price fills the same
        calendar space more profitably, and the per-duration tiers let you shape that margin exactly how you
        want it.
      </li>
      <li>
        <strong>One event, one calendar, one set of reports.</strong> The 60, 90 and 120 minute versions
        are the same event, so bookings, photos, waivers and event tracking stay together.
      </li>
      <li>
        <strong>A natural upsell at checkout.</strong> The duration choice sits right after the time slot,
        when the customer has already picked their date. A clearly priced longer option sells itself.
      </li>
      <li>
        <strong>Availability you can trust.</strong> A two hour booking at 2pm blocks the room until 4pm,
        not 3pm, so you never double book the room after a long session.
      </li>
    </ul>

    <h2>A few ways operators are using it</h2>
    <p>
      <strong>Axe throwing and lane based venues:</strong> 60 minutes as the default, with 90 and 120 minute
      options. Price the lane per person on the hour and per group on the longer sessions, so a party of ten
      books the two hour slot without a per head penalty.
    </p>
    <p>
      <strong>Escape rooms:</strong> the standard 60 minute experience alongside a 75 minute extended cut with
      an extra chapter, at a premium, with the standard price untouched.
    </p>
    <p>
      <strong>VR and simulator bays:</strong> 15, 30 and 60 minute sessions on the same bay. A 15 minute
      default keeps the walk in price low; the longer sessions carry the margin.
    </p>
    <p>
      <strong>Party rooms and private hire:</strong> two hour and three hour packages on one room, each with
      its own participant limits and deposit rules, booked from a single slot.
    </p>

    <h2>Where you can configure durations</h2>
    <ul>
      <li>Event settings → Pricing</li>
      <li>Custom pricing on individual booking slots</li>
      <li>Schedule templates → Custom pricing</li>
    </ul>

    <h2>To enable it</h2>
    <p>
      Go to <strong>Events → Event settings → Edit event → Pricing</strong>.
    </p>
    <p>
      Under Pricing categories, turn on <strong>Enable multiple durations</strong>, then use
      <strong>Add duration</strong> to create your additional lengths. Select any duration to configure its
      pricing independently. You can also choose which duration should be the default.
    </p>
    <figure class="post-figure">
      <img
        src="/img/blog/multiple-event-durations-pricing-categories.png"
        alt="The Pricing categories card on an event's Pricing tab with Enable multiple durations switched on, duration pills for 60, 90 and 120 minutes, and the Adult and Child categories priced for the selected 120 minute duration"
        width="2000"
        height="922"
        loading="lazy"
      />
      <figcaption>Each duration pill holds its own categories, tiers and participant limits. Here the 120 minute duration is selected.</figcaption>
    </figure>
    <p>
      Durations are optional and off by default, so nothing changes for your events unless you turn them on.
    </p>

    <h2>Behind the scenes</h2>
    <p>
      There is quite a bit happening with this one. Once a duration is selected, OTC stores the actual
      booked length and uses the correct end time for availability, resource capacity, autoblocking, and
      scheduling calculations. When several groups share one public slot, the slot spans its longest group.
      Cancel or move a booking and the slot goes back to its default length, so the next customer sees the
      standard window.
    </p>
    <p>
      This means you can now sell different time lengths of the same experience without creating separate
      events just to support different lengths or pricing.
    </p>
    <p>
      The full setup walkthrough is in the
      <a href="https://docs.offthecouch.io/articles/pricing-and-deposits" target="_blank" rel="noopener">pricing and deposits guide</a>.
      As always, if you notice anything unexpected or have ideas for improving it, let us know in the
      <a href="https://discord.gg/752E9HM6tt" target="_blank" rel="noopener">feedback channel</a>
      or submit an enhancement ticket.
    </p>
  </div>
</template>

<script>
export default {
  name: 'BlogPostMultipleEventDurations',
};
</script>
